/**
 * DocumentsPage.tsx
 *
 * Practice-wide documents library — search, filter, manage patient documents,
 * document packets, tags/keywords, bulk actions, and enhanced upload.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { usePageContext } from '@/hooks/usePageContext';
import { PatientLink } from '@/components/ui/PatientLink';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Download,
  FileText,
  FileImage,
  File,
  Trash2,
  Upload,
  Package,
  Tag,
  X,
  CheckSquare,
  Square,
  Pencil,
  Plus,
  Settings,
} from 'lucide-react';
import {
  getDocuments,
  deleteDocument,
  updateDocument,
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getPackets,
  createPacket,
  updatePacket,
  deletePacket,
  uploadDocument,
  type Document,
  type DocumentFilters,
  type DocumentFormat,
  type DocumentCategory,
  type DocumentPacket,
  type SigningStatus,
} from '@/services/documents.service';
import { searchPatients } from '@/services/patients.service';
import type { PatientSummary } from '@/types/patient.types';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableAction, DataTableColumn } from '@/components/ui/DataTable';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';
import { AutoGrowTextarea } from '@/components/ui/Textarea';

// ─── Filter configs for sidebar ────────────────────────────────────────────────

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search documents...' },
  { key: 'category', label: 'Category', type: 'select', allLabel: 'All categories' },
  { key: 'format', label: 'Format', type: 'select', allLabel: 'All formats' },
  { key: 'signingStatus', label: 'Signing Status', type: 'select', allLabel: 'All' },
];

const FORMAT_OPTIONS: FilterOption[] = [
  { value: 'pdf', label: 'PDF' },
  { value: 'docx', label: 'DOCX' },
  { value: 'doc', label: 'DOC' },
  { value: 'image', label: 'Image' },
  { value: 'html', label: 'HTML' },
  { value: 'other', label: 'Other' },
];

const SIGNING_STATUS_OPTIONS: FilterOption[] = [
  { value: 'unsigned', label: 'Unsigned' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'declined', label: 'Declined' },
  { value: 'voided', label: 'Voided' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function FileIcon({ format }: { format: DocumentFormat }) {
  if (format === 'pdf') return <FileText className="h-4 w-4 text-red-500" />;
  if (format === 'image') return <FileImage className="h-4 w-4 text-purple-500" />;
  if (format === 'docx' || format === 'doc') return <FileText className="h-4 w-4 text-blue-500" />;
  return <File className="h-4 w-4 text-slate-400" />;
}

const SIGNING_STYLES: Record<SigningStatus, string> = {
  unsigned: 'bg-slate-100 text-slate-600',
  pending: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  voided: 'bg-gray-100 text-gray-500',
};

// Placeholder tags for demo purposes (the old PHP had keywords/tags)
const AVAILABLE_TAGS = ['consent', 'insurance', 'lab-results', 'imaging', 'referral', 'intake', 'discharge', 'billing'];

// ─── Upload Modal ─────────────────────────────────────────────────────────────

function UploadModal({
  categories,
  onClose,
  onUploaded,
}: {
  categories: { categoryId: number; categoryName: string }[];
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [debouncedPatientSearch, setDebouncedPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientSummary | null>(null);
  const [showPatientResults, setShowPatientResults] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePatientSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPatientSearch(e.target.value);
    setSelectedPatient(null);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedPatientSearch(e.target.value);
      setShowPatientResults(true);
    }, 300);
  }, []);

  useEffect(() => () => { if (searchTimer.current) clearTimeout(searchTimer.current); }, []);

  const { data: patients = [] } = useQuery({
    queryKey: ['patient-search-upload', debouncedPatientSearch],
    queryFn: () => searchPatients(debouncedPatientSearch),
    enabled: debouncedPatientSearch.length >= 2,
    staleTime: 10_000,
  });

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('No file selected');
      return uploadDocument(
        selectedPatient?.patientId ?? null,
        file,
        {
          title: title.trim() || file.name,
          categoryId: categoryId ? parseInt(categoryId, 10) : undefined,
          notes: [description, ...selectedTags.map((t) => `#${t}`)].filter(Boolean).join(' ') || undefined,
        },
        (percent) => setUploadProgress(percent),
      );
    },
    onSuccess: () => {
      onUploaded();
      onClose();
    },
  });

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      if (!title) setTitle(droppedFile.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      if (!title) setTitle(selected.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="rounded-xl bg-white shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-800">Upload Document</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 flex-1 space-y-4">
          {/* Drag and drop area */}
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
              isDragging ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:border-blue-300',
              file ? 'border-green-300 bg-green-50' : '',
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Upload className={cn('h-8 w-8 mx-auto mb-2', file ? 'text-green-500' : 'text-slate-400')} />
            {file ? (
              <p className="text-sm text-green-700 font-medium">{file.name}</p>
            ) : (
              <>
                <p className="text-sm text-slate-600">Drag and drop a file here, or click to browse</p>
                <p className="text-xs text-slate-400 mt-1">PDF, DOCX, DOC, Images supported</p>
              </>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
            <AutoGrowTextarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No Category</option>
              {categories.map((cat) => (
                <option key={cat.categoryId} value={cat.categoryId}>{cat.categoryName}</option>
              ))}
            </select>
          </div>

          {/* Patient assignment */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Assign to Patient</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={patientSearch}
                onChange={handlePatientSearchChange}
                placeholder="Search patient..."
                className="w-full rounded border border-slate-300 pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {showPatientResults && patients.length > 0 && !selectedPatient && (
              <div className="mt-1 border border-slate-200 rounded bg-white shadow-lg max-h-32 overflow-y-auto">
                {patients.map((p) => (
                  <button
                    key={p.patientId}
                    type="button"
                    onClick={() => {
                      setSelectedPatient(p);
                      setPatientSearch(p.displayName);
                      setShowPatientResults(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-primary/5 border-b border-slate-100 last:border-0"
                  >
                    {p.displayName}
                  </button>
                ))}
              </div>
            )}
            {selectedPatient && (
              <div className="mt-1 text-xs text-blue-600">
                Assigned to: {selectedPatient.displayName}
              </div>
            )}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Tags</label>
            <div className="flex flex-wrap gap-1.5">
              {AVAILABLE_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
                    selectedTags.includes(tag)
                      ? 'bg-primary/10 text-primary border border-primary'
                      : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200',
                  )}
                >
                  <Tag className="h-3 w-3" />
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Upload progress */}
          {uploadProgress !== null && (
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => uploadMutation.mutate()}
            disabled={!file || uploadMutation.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
          </button>
        </div>

        {uploadMutation.isError && (
          <div className="px-6 pb-4 text-sm text-red-600">
            {uploadMutation.error instanceof Error ? uploadMutation.error.message : 'Upload failed.'}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Edit Document Modal ─────────────────────────────────────────────────────

function EditDocumentModal({
  doc,
  categories,
  onClose,
  onSaved,
}: {
  doc: Document;
  categories: { categoryId: number; categoryName: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(doc.title);
  const [description, setDescription] = useState(doc.description ?? '');
  const [editCategoryId, setEditCategoryId] = useState(doc.categoryId ? String(doc.categoryId) : '');
  const [selectedTags, setSelectedTags] = useState<string[]>(() => {
    if (!doc.notes) return [];
    const matches = doc.notes.match(/#[\w-]+/g);
    return matches ? matches.map((m) => m.slice(1)) : [];
  });
  const [patientSearch, setPatientSearch] = useState(doc.patientName ?? '');
  const [debouncedPatientSearch, setDebouncedPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientSummary | null>(
    doc.patientId ? ({ patientId: doc.patientId, displayName: doc.patientName ?? '' } as PatientSummary) : null,
  );
  const [showPatientResults, setShowPatientResults] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePatientSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPatientSearch(e.target.value);
    setSelectedPatient(null);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedPatientSearch(e.target.value);
      setShowPatientResults(true);
    }, 300);
  }, []);

  useEffect(() => () => { if (searchTimer.current) clearTimeout(searchTimer.current); }, []);

  const { data: patients = [] } = useQuery({
    queryKey: ['patient-search-edit', debouncedPatientSearch],
    queryFn: () => searchPatients(debouncedPatientSearch),
    enabled: debouncedPatientSearch.length >= 2,
    staleTime: 10_000,
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      const notesValue = [
        description,
        ...selectedTags.map((t) => `#${t}`),
      ].filter(Boolean).join(' ') || undefined;

      return updateDocument(doc.documentId, {
        title: title.trim() || doc.title,
        description: description.trim() || undefined,
        categoryId: editCategoryId ? parseInt(editCategoryId, 10) : undefined,
        notes: notesValue,
      });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="rounded-xl bg-white shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-800">Edit Document</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 flex-1 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
            <AutoGrowTextarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
            <select
              value={editCategoryId}
              onChange={(e) => setEditCategoryId(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No Category</option>
              {categories.map((cat) => (
                <option key={cat.categoryId} value={cat.categoryId}>{cat.categoryName}</option>
              ))}
            </select>
          </div>

          {/* Patient assignment */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Assign to Patient</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={patientSearch}
                onChange={handlePatientSearchChange}
                placeholder="Search patient..."
                className="w-full rounded border border-slate-300 pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {showPatientResults && patients.length > 0 && !selectedPatient && (
              <div className="mt-1 border border-slate-200 rounded bg-white shadow-lg max-h-32 overflow-y-auto">
                {patients.map((p) => (
                  <button
                    key={p.patientId}
                    type="button"
                    onClick={() => {
                      setSelectedPatient(p);
                      setPatientSearch(p.displayName);
                      setShowPatientResults(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-primary/5 border-b border-slate-100 last:border-0"
                  >
                    {p.displayName}
                  </button>
                ))}
              </div>
            )}
            {selectedPatient && (
              <div className="mt-1 text-xs text-blue-600">
                Assigned to: {selectedPatient.displayName}
              </div>
            )}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Tags</label>
            <div className="flex flex-wrap gap-1.5">
              {AVAILABLE_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
                    selectedTags.includes(tag)
                      ? 'bg-primary/10 text-primary border border-primary'
                      : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200',
                  )}
                >
                  <Tag className="h-3 w-3" />
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {updateMutation.isError && (
          <div className="px-6 pb-4 text-sm text-red-600">
            {updateMutation.error instanceof Error ? updateMutation.error.message : 'Update failed.'}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Packet Modal (Create / Edit) ────────────────────────────────────────────

function PacketModal({
  packet,
  allDocuments,
  onClose,
  onSaved,
}: {
  packet: DocumentPacket | null; // null = create mode
  allDocuments: Document[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(packet?.name ?? '');
  const [packetDescription, setPacketDescription] = useState(packet?.description ?? '');
  const [active, setActive] = useState(packet?.active ?? true);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<number>>(
    new Set(packet?.documents.map((d) => d.documentId) ?? []),
  );
  const [docSearch, setDocSearch] = useState('');

  const isEdit = packet !== null;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        description: packetDescription.trim() || undefined,
        active,
        documentIds: Array.from(selectedDocIds),
      };
      return isEdit ? updatePacket(packet.packetId, payload) : createPacket(payload);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const toggleDoc = (id: number) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredDocs = docSearch
    ? allDocuments.filter((d) => d.title.toLowerCase().includes(docSearch.toLowerCase()))
    : allDocuments;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="rounded-xl bg-white shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-800">{isEdit ? 'Edit Packet' : 'Create Packet'}</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 flex-1 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Packet name"
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
            <AutoGrowTextarea
              value={packetDescription}
              onChange={(e) => setPacketDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Active toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-slate-700">Active</span>
          </label>

          {/* Document selector */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Documents ({selectedDocIds.size} selected)
            </label>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={docSearch}
                onChange={(e) => setDocSearch(e.target.value)}
                placeholder="Filter documents..."
                className="w-full rounded border border-slate-300 pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="border border-slate-200 rounded max-h-40 overflow-y-auto">
              {filteredDocs.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-400">No documents found.</div>
              ) : (
                filteredDocs.map((d) => (
                  <button
                    key={d.documentId}
                    type="button"
                    onClick={() => toggleDoc(d.documentId)}
                    className={cn(
                      'w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-slate-50 border-b border-slate-100 last:border-0',
                      selectedDocIds.has(d.documentId) && 'bg-primary/10',
                    )}
                  >
                    {selectedDocIds.has(d.documentId)
                      ? <CheckSquare className="h-4 w-4 text-primary flex-shrink-0" />
                      : <Square className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    }
                    <FileIcon format={d.format} />
                    <span className="truncate">{d.title}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={!name.trim() || saveMutation.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Packet'}
          </button>
        </div>

        {saveMutation.isError && (
          <div className="px-6 pb-4 text-sm text-red-600">
            {saveMutation.error instanceof Error ? saveMutation.error.message : 'Save failed.'}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Category Management Modal ───────────────────────────────────────────────

function ManageCategoriesModal({
  categories,
  onClose,
  onChanged,
}: {
  categories: DocumentCategory[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const createMutation = useMutation({
    mutationFn: () => createCategory({ categoryName: newName.trim(), description: newDescription.trim() || undefined }),
    onSuccess: () => {
      setIsAdding(false);
      setNewName('');
      setNewDescription('');
      onChanged();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (id: number) => updateCategory(id, {
      categoryName: editName.trim(),
      description: editDescription.trim() || null,
      active: editActive,
    }),
    onSuccess: () => {
      setEditingId(null);
      onChanged();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteCategory(id),
    onSuccess: () => {
      setDeleteConfirmId(null);
      onChanged();
    },
  });

  const startEdit = (cat: DocumentCategory) => {
    setEditingId(cat.categoryId);
    setEditName(cat.categoryName);
    setEditDescription(cat.description ?? '');
    setEditActive(cat.active);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="rounded-xl bg-white shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-800">Manage Categories</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-4 flex-1 space-y-2">
          {categories.length === 0 && !isAdding && (
            <p className="text-sm text-slate-400 text-center py-4">No categories yet.</p>
          )}

          {categories.map((cat) => (
            <div key={cat.categoryId} className="border border-slate-200 rounded-lg p-3">
              {editingId === cat.categoryId ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Category name"
                  />
                  <input
                    type="text"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Description (optional)"
                  />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editActive}
                      onChange={(e) => setEditActive(e.target.checked)}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs text-slate-600">Active</span>
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => updateMutation.mutate(cat.categoryId)}
                      disabled={!editName.trim() || updateMutation.isPending}
                      className="rounded bg-primary px-3 py-1 text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="rounded border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : deleteConfirmId === cat.categoryId ? (
                <div className="space-y-2">
                  <p className="text-sm text-slate-700">Delete <strong>{cat.categoryName}</strong>?</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => deleteMutation.mutate(cat.categoryId)}
                      disabled={deleteMutation.isPending}
                      className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {deleteMutation.isPending ? 'Deleting...' : 'Confirm Delete'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmId(null)}
                      className="rounded border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-slate-800">{cat.categoryName}</span>
                    {cat.description && <p className="text-xs text-slate-500">{cat.description}</p>}
                    {!cat.active && <span className="text-xs text-slate-400 ml-2">(inactive)</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => startEdit(cat)}
                      className="p-1 text-slate-400 hover:text-primary"
                      title="Edit category"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmId(cat.categoryId)}
                      className="p-1 text-slate-400 hover:text-red-600"
                      title="Delete category"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Add new category */}
          {isAdding ? (
            <div className="border border-blue-200 rounded-lg p-3 bg-blue-50 space-y-2">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Category name"
                autoFocus
              />
              <input
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Description (optional)"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => createMutation.mutate()}
                  disabled={!newName.trim() || createMutation.isPending}
                  className="rounded bg-primary px-3 py-1 text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Adding...' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={() => { setIsAdding(false); setNewName(''); setNewDescription(''); }}
                  className="rounded border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/90 font-medium pt-2"
            >
              <Plus className="h-4 w-4" />
              Add Category
            </button>
          )}
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DocumentsPage() {
  const queryClient = useQueryClient();
  const updateAIContext = usePageContext('Documents');

  // Tab state: "documents" | "packets"
  const [activeTab, setActiveTab] = useState<'documents' | 'packets'>('documents');

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [formatFilter, setFormatFilter] = useState<DocumentFormat | ''>('');
  const [signingStatusFilter, setSigningStatusFilter] = useState<SigningStatus | ''>('');
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [editTarget, setEditTarget] = useState<Document | null>(null);
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [showPacketModal, setShowPacketModal] = useState(false);
  const [editPacket, setEditPacket] = useState<DocumentPacket | null>(null);
  const [deletePacketTarget, setDeletePacketTarget] = useState<DocumentPacket | null>(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (searchTimer.current) clearTimeout(searchTimer.current); }, []);
  useEffect(() => { setPage(1); }, [categoryFilter, formatFilter, signingStatusFilter]);

  const filters: DocumentFilters = {};
  if (debouncedSearch) filters.search = debouncedSearch;
  if (categoryFilter) filters.categoryId = parseInt(categoryFilter, 10);
  if (formatFilter) filters.format = formatFilter;

  const { data, isLoading } = useQuery({
    queryKey: ['documents', filters, page],
    queryFn: () => getDocuments(filters, { page, perPage }),
    staleTime: 30_000,
  });

  const { data: categories } = useQuery({
    queryKey: ['document-categories'],
    queryFn: getCategories,
    staleTime: 300_000,
  });

  const { data: packets = [], isLoading: packetsLoading } = useQuery({
    queryKey: ['document-packets'],
    queryFn: getPackets,
    staleTime: 300_000,
    enabled: activeTab === 'packets',
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteDocument(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setDeleteTarget(null);
    },
  });

  const deletePacketMutation = useMutation({
    mutationFn: (id: number) => deletePacket(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-packets'] });
      setDeletePacketTarget(null);
    },
  });

  // Fetch all documents for the packet document selector
  const { data: allDocsData } = useQuery({
    queryKey: ['documents-all-for-packets'],
    queryFn: () => getDocuments({}, { page: 1, perPage: 500 }),
    staleTime: 60_000,
    enabled: showPacketModal || editPacket !== null,
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selectedIds);
      for (const id of ids) {
        await deleteDocument(id as number);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
    },
  });

  const documents = data?.data ?? [];

  // Client-side signing status filter (if API doesn't support it)
  const filteredDocuments = useMemo(() => {
    let result = documents;
    if (signingStatusFilter) {
      result = result.filter((doc) => doc.signingStatus === signingStatusFilter);
    }
    return result;
  }, [documents, signingStatusFilter]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const allDocs = filteredDocuments;
    const uniqueCategories = new Set(allDocs.map((d) => d.categoryName).filter(Boolean));
    const pendingSigning = allDocs.filter((d) => d.signingStatus === 'pending').length;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recentUploads = allDocs.filter((d) => {
      if (!d.dateUploaded) return false;
      return new Date(d.dateUploaded) >= sevenDaysAgo;
    }).length;
    return {
      total: data?.meta.total ?? 0,
      categories: uniqueCategories.size,
      pendingSigning,
      recentUploads,
    };
  }, [filteredDocuments, data?.meta.total]);

  // ── AI page context ────────────────────────────────────────────────────────
  useEffect(() => {
    updateAIContext({
      filters: `Tab: ${activeTab}, Search: ${debouncedSearch || 'none'}, Format: ${formatFilter || 'all'}`,
      dataSummary: `${data?.meta.total ?? 0} documents, ${packets.length} packets`,
    });
  }, [activeTab, debouncedSearch, formatFilter, data?.meta.total, packets.length, updateAIContext]);

  // Extract tags from document notes for display
  const getDocTags = (doc: Document): string[] => {
    if (!doc.notes) return [];
    const matches = doc.notes.match(/#[\w-]+/g);
    return matches ? matches.map((m) => m.slice(1)) : [];
  };

  // ── Sidebar filter options / values / handlers ─────────────────────────────

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({
      category: (categories ?? []).map((cat) => ({
        value: String(cat.categoryId),
        label: cat.categoryName,
      })),
      format: FORMAT_OPTIONS,
      signingStatus: SIGNING_STATUS_OPTIONS,
    }),
    [categories],
  );

  const sidebarValues = useMemo<Record<string, FilterValue>>(
    () => ({
      search: debouncedSearch ? [debouncedSearch] : [],
      category: categoryFilter ? [categoryFilter] : [],
      format: formatFilter ? [formatFilter] : [],
      signingStatus: signingStatusFilter ? [signingStatusFilter] : [],
    }),
    [debouncedSearch, categoryFilter, formatFilter, signingStatusFilter],
  );

  const handleSidebarChange = useCallback((filterKey: string, value: FilterValue) => {
    switch (filterKey) {
      case 'search': {
        const searchVal = Array.isArray(value) && value[0] ? value[0] : '';
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
          setDebouncedSearch(searchVal);
          setPage(1);
        }, 300);
        break;
      }
      case 'category':
        if (Array.isArray(value)) {
          setCategoryFilter(value[0] ?? '');
        }
        break;
      case 'format':
        if (Array.isArray(value)) {
          setFormatFilter((value[0] as DocumentFormat) ?? '');
        }
        break;
      case 'signingStatus':
        if (Array.isArray(value)) {
          setSigningStatusFilter((value[0] as SigningStatus) ?? '');
        }
        break;
    }
  }, []);

  const handleSidebarClear = useCallback((filterKey: string) => {
    switch (filterKey) {
      case 'search':
        setDebouncedSearch('');
        break;
      case 'category':
        setCategoryFilter('');
        break;
      case 'format':
        setFormatFilter('');
        break;
      case 'signingStatus':
        setSigningStatusFilter('');
        break;
    }
  }, []);

  // ── DataTable columns ──────────────────────────────────────────────────────

  const columns = useMemo<DataTableColumn<Document>[]>(() => [
    {
      key: 'title',
      header: 'Document',
      sortable: true,
      render: (doc) => (
        <div className="flex items-center gap-2">
          <FileIcon format={doc.format} />
          <div>
            <p className="font-medium text-slate-800 leading-snug">{doc.title}</p>
            {doc.description && (
              <p className="text-xs text-slate-400 truncate max-w-48">{doc.description}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'patientName',
      header: 'Patient',
      sortable: true,
      render: (doc) =>
        doc.patientId ? (
          <PatientLink patientId={doc.patientId} patientName={doc.patientName ?? `#${doc.patientId}`} className="text-blue-600 hover:underline">
            {doc.patientName ?? `#${doc.patientId}`}
          </PatientLink>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'categoryName',
      header: 'Category',
      sortable: true,
      render: (doc) => <span className="text-slate-600">{doc.categoryName ?? '—'}</span>,
    },
    {
      key: 'tags',
      header: 'Tags',
      render: (doc) => {
        const tags = getDocTags(doc);
        return tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {tags.map((tag) => (
              <span key={tag} className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                {tag}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        );
      },
    },
    {
      key: 'format',
      header: 'Format',
      sortable: true,
      render: (doc) => (
        <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 uppercase">
          {doc.format}
        </span>
      ),
    },
    {
      key: 'signingStatus',
      header: 'Signing Status',
      sortable: true,
      render: (doc) => (
        <span className={cn(
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize',
          SIGNING_STYLES[doc.signingStatus],
        )}>
          {doc.signingStatus}
        </span>
      ),
    },
    {
      key: 'dateUploaded',
      header: 'Uploaded',
      sortable: true,
      render: (doc) => <span className="text-slate-600 whitespace-nowrap">{formatDate(doc.dateUploaded)}</span>,
    },
  ], []);

  const rowActions = useMemo<DataTableAction<Document>[]>(() => [
    {
      label: 'Edit',
      onClick: (doc) => setEditTarget(doc),
    },
    {
      label: 'Download',
      onClick: (doc) => {
        window.open(doc.downloadUrl, '_blank');
      },
    },
    {
      label: 'Delete',
      variant: 'danger',
      onClick: (doc) => setDeleteTarget(doc),
    },
  ], []);

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={FILTER_CONFIGS}
            values={sidebarValues}
            options={sidebarOptions}
            onChange={handleSidebarChange}
            onClear={handleSidebarClear}
            showAIAssistant={false}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-6 p-6">
            <PageHeader
              title="Documents"
              subtitle="Patient documents and files."
              actions={(
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => setShowManageCategories(true)}
                  >
                    <Settings className="mr-1 h-4 w-4" />
                    Categories
                  </Button>
                  <Button
                    onClick={() => setShowUpload(true)}
                  >
                    <Upload className="mr-1 h-4 w-4" />
                    Upload Document
                  </Button>
                </div>
              )}
            />

            <MetricCardGrid columns={4}>
              <StatCard label="Total Documents" value={stats.total} />
              <StatCard label="Categories" value={stats.categories} />
              <StatCard label="Pending Signing" value={stats.pendingSigning} />
              <StatCard label="Recent Uploads" value={stats.recentUploads} />
            </MetricCardGrid>

            <div className="flex flex-wrap items-center gap-3">
              {/* Tabs */}
              <button
                type="button"
                onClick={() => setActiveTab('documents')}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                  activeTab === 'documents'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100',
                )}
              >
                <span className="inline-flex items-center gap-1.5">
                  <FileText className="h-4 w-4" />
                  Documents
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('packets')}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                  activeTab === 'packets'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100',
                )}
              >
                <span className="inline-flex items-center gap-1.5">
                  <Package className="h-4 w-4" />
                  Packets
                </span>
              </button>

              {activeTab === 'documents' && (
                <>
                  {selectedIds.size > 0 && (
                    <>
                      <span className="text-sm font-medium text-blue-700">{selectedIds.size} selected</span>
                      <Button
                        variant="ghost"
                        onClick={() => setBulkDeleteConfirm(true)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="mr-1 h-3.5 w-3.5" />
                        Delete Selected
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          alert('Bulk download: This feature will download ' + selectedIds.size + ' document(s).');
                        }}
                      >
                        <Download className="mr-1 h-3.5 w-3.5" />
                        Download Selected
                      </Button>
                      <button
                        type="button"
                        onClick={() => setSelectedIds(new Set())}
                        className="text-xs text-slate-500 hover:text-slate-700 underline"
                      >
                        Clear Selection
                      </button>
                    </>
                  )}

                  <span className="ml-auto text-sm text-slate-500">
                    {data?.meta.total ?? 0} documents
                  </span>
                </>
              )}

              {activeTab === 'packets' && (
                <>
                  <span className="text-sm text-slate-500">{packets.length} packet{packets.length !== 1 ? 's' : ''}</span>
                  <Button
                    className="ml-auto"
                    onClick={() => { setEditPacket(null); setShowPacketModal(true); }}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Create Packet
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            {activeTab === 'documents' && (
              <DataTable<Document>
                data={filteredDocuments}
                columns={columns}
                keyField="documentId"
                loading={isLoading}
                rowActions={rowActions}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                page={page}
                pageSize={perPage}
                total={data?.meta.total ?? 0}
                onPageChange={setPage}
                emptyTitle="No documents found"
                emptyDescription="Try adjusting your filters or upload a new document."
              />
            )}

            {activeTab === 'packets' && (
              <>
                {packetsLoading ? (
                  <div className="py-8 text-center text-sm text-slate-500">Loading packets...</div>
                ) : packets.length === 0 ? (
                  <div className="py-12 text-center">
                    <Package className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No document packets found.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {packets.map((pkt) => (
                      <div
                        key={pkt.packetId}
                        className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col gap-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-sm font-semibold text-slate-800">{pkt.name}</h3>
                          <span className={cn(
                            'flex-shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                            pkt.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500',
                          )}>
                            {pkt.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {pkt.description && (
                          <p className="text-xs text-slate-500">{pkt.description}</p>
                        )}
                        <div className="text-xs text-slate-600">
                          <span className="font-medium">{pkt.documents.length}</span> document{pkt.documents.length !== 1 ? 's' : ''}
                        </div>
                        {pkt.documents.length > 0 && (
                          <div className="space-y-1">
                            {pkt.documents.map((doc) => (
                              <div key={doc.documentId} className="flex items-center gap-2 text-xs text-slate-500">
                                <FileIcon format={doc.format} />
                                <span className="truncate">{doc.title}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
                          <button
                            type="button"
                            onClick={() => { setEditPacket(pkt); setShowPacketModal(true); }}
                            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-primary font-medium"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeletePacketTarget(pkt)}
                            className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </ListPageLayout>

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="rounded-xl bg-white shadow-xl p-6 w-full max-w-sm mx-4">
            <h2 className="text-base font-semibold text-slate-800 mb-2">Delete Document?</h2>
            <p className="text-sm text-slate-600 mb-4">
              Delete <strong>{deleteTarget.title}</strong>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(deleteTarget.documentId)}
                disabled={deleteMutation.isPending}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirmation */}
      {bulkDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="rounded-xl bg-white shadow-xl p-6 w-full max-w-sm mx-4">
            <h2 className="text-base font-semibold text-slate-800 mb-2">Bulk Delete?</h2>
            <p className="text-sm text-slate-600 mb-4">
              Delete <strong>{selectedIds.size}</strong> selected document{selectedIds.size !== 1 ? 's' : ''}? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setBulkDeleteConfirm(false)}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => bulkDeleteMutation.mutate()}
                disabled={bulkDeleteMutation.isPending}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {bulkDeleteMutation.isPending ? 'Deleting...' : 'Delete All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <UploadModal
          categories={categories ?? []}
          onClose={() => setShowUpload(false)}
          onUploaded={() => queryClient.invalidateQueries({ queryKey: ['documents'] })}
        />
      )}

      {/* Edit document modal */}
      {editTarget && (
        <EditDocumentModal
          doc={editTarget}
          categories={categories ?? []}
          onClose={() => setEditTarget(null)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['documents'] })}
        />
      )}

      {/* Manage categories modal */}
      {showManageCategories && (
        <ManageCategoriesModal
          categories={categories ?? []}
          onClose={() => setShowManageCategories(false)}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['document-categories'] })}
        />
      )}

      {/* Packet create/edit modal */}
      {showPacketModal && (
        <PacketModal
          packet={editPacket}
          allDocuments={allDocsData?.data ?? []}
          onClose={() => { setShowPacketModal(false); setEditPacket(null); }}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['document-packets'] })}
        />
      )}

      {/* Packet delete confirmation */}
      {deletePacketTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="rounded-xl bg-white shadow-xl p-6 w-full max-w-sm mx-4">
            <h2 className="text-base font-semibold text-slate-800 mb-2">Delete Packet?</h2>
            <p className="text-sm text-slate-600 mb-4">
              Delete <strong>{deletePacketTarget.name}</strong>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeletePacketTarget(null)}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deletePacketMutation.mutate(deletePacketTarget.packetId)}
                disabled={deletePacketMutation.isPending}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deletePacketMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
